import { useEffect, useMemo, useState } from 'react';

import { Col, Loading, Modal, Row, Stepper, handleAxiosError, message } from 'ui';
import { isDefaultResponseErrorDataObject } from 'common/services';
import { useUser } from 'features/auth';
import {
  type DeliveryFormData,
  DeliveryInfo,
  type InfoFormData,
  type PersonalFormData,
  PersonalInfo,
  SubscribeInfo,
  SubscribeResult,
  Summary,
} from './components';
import { useSubscribeQuery } from './queries';
import type { Gender } from './types';
import { convertFeetToInches } from './utils/inches';

const minStep = 0;
const maxStep = 4;

type P = {
  isOpened: boolean;
  onCancel: () => void;
  onSuccess: () => void;
  programId: string;
};

const Subscribe = ({ isOpened, onCancel, onSuccess, programId }: P) => {
  const { railsUserData: user } = useUser();
  const {
    error: subscribeError,
    isPending: isSubscribing,
    isSuccess: isSubscribeSuccess,
    mutate: subscribe,
  } = useSubscribeQuery();

  const [step, setStep] = useState(minStep);
  const [data, setData] = useState<{
    delivery?: DeliveryFormData;
    info?: InfoFormData;
    personal?: PersonalFormData;
  }>({});

  const gender: Gender | undefined = useMemo(() => {
    switch (user.gender?.name) {
      case 'Female':
        return 'female';
      case 'Male':
        return 'male';
      // TODO: dont remove it, eslint has error if we remove it
      case undefined:
        return undefined;
      default:
        return undefined;
    }
  }, [user.gender]);

  const goToNextStep = useMemo(
    () => () => {
      setStep((currentStep) => Math.min(currentStep + 1, maxStep));
    },
    [setStep],
  );
  const goToPreviousStep = useMemo(
    () => () => {
      setStep((currentStep) => Math.max(currentStep - 1, minStep));
    },
    [setStep],
  );

  useEffect(() => {
    if (isSubscribeSuccess && step === 3) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- TODO: fix
      goToNextStep();
    }
  }, [subscribeError, isSubscribeSuccess, goToNextStep, step]);

  const handleInfo = (values: InfoFormData) => {
    setData({
      ...data,
      info: values,
    });
    goToNextStep();
  };

  const handlePersonalInfo = (values: PersonalFormData) => {
    setData({
      ...data,
      personal: values,
    });
    goToNextStep();
  };

  const handleDeliveryInfo = (values: DeliveryFormData) => {
    setData({
      ...data,
      delivery: values,
    });
    goToNextStep();
  };

  const handleSummary = () => {
    if (data.info && data.delivery && data.personal) {
      subscribe(
        {
          deliveryData: data.delivery,
          infoData: data.info,
          personalData: {
            ...data.personal,
            heightInImperial:
              convertFeetToInches(data.personal.heightInImperial.feet) + data.personal.heightInImperial.inches,
          },
          programId,
        },
        {
          onError(error) {
            if (
              Number(error.response?.status) < 500 &&
              isDefaultResponseErrorDataObject(error.response?.data) &&
              // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
              error.response?.data.message
            ) {
              // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
              message.error(error.response?.data.message);
            } else {
              handleAxiosError(error);
            }
          },
        },
      );
    }
  };

  const handleBackClick = () => {
    goToPreviousStep();
  };

  const generateStepperContent = (stepOption: number) => {
    switch (stepOption) {
      case 0:
        return <SubscribeInfo initialData={data.info} onSubmit={handleInfo} />;
      case 1:
        return (
          <PersonalInfo
            initialData={data.personal}
            onBack={handleBackClick}
            onSubmit={handlePersonalInfo}
            user={{ firstName: user.firstName, gender, lastName: user.lastName }}
          />
        );
      case 2:
        return (
          <DeliveryInfo
            initialData={data.delivery}
            onBack={handleBackClick}
            onSubmit={handleDeliveryInfo}
            user={{
              apt: user.apt,
              city: user.city,
              country: user.country,
              email: user.email,
              firstName: user.firstName,
              lastName: user.lastName,
              phone: user.phone,
              state: user.state,
              street: user.street,
              zip: user.zip,
            }}
          />
        );
      case 3:
        return <Summary data={data} onBack={handleBackClick} onSubmit={handleSummary} />;
      default:
        return <SubscribeResult key="result" onClose={onCancel} />;
    }
  };

  const handleModalClose = () => {
    setStep(minStep);
    setData({});

    if (step === maxStep) {
      onSuccess();
    } else {
      onCancel();
    }
  };

  return (
    <Modal isOpened={isOpened} maskClosable={false} onCancel={handleModalClose} title="Order request" width="medium">
      <Modal.HugContent>
        {isSubscribing ? (
          <Row justify="center">
            <Col>
              <Loading />
            </Col>
          </Row>
        ) : (
          <Stepper
            currentStep={step}
            getStep={generateStepperContent}
            hideSteps={step === maxStep}
            key="stepper"
            numberOfSteps={maxStep}
            onNavigateTo={(nextStep) => {
              setStep(nextStep);
            }}
          />
        )}
      </Modal.HugContent>
    </Modal>
  );
};

export default Subscribe;
